import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export const NAME_MAX_LENGTH = 100;
export const LABEL_MAX_LENGTH = 200;
export const SECTION_MAX_LENGTH = 100;
export const INSTRUCTIONS_MAX_LENGTH = 2000;
export const MAX_ITEMS = 200;

export interface ChecklistTemplateItem {
  section?: string | null;
  label: string;
  instructions?: string | null;
  position: number;
  required: boolean;
  requires_photo: boolean;
}

export interface ChecklistSection<T extends ChecklistTemplateItem> {
  key: string;
  section: string | undefined;
  items: T[];
}

export function groupBySection<T extends ChecklistTemplateItem>(
  items: T[],
): ChecklistSection<T>[] {
  const groups = new Map<string | undefined, T[]>();

  for (const item of items) {
    const key = item.section || undefined;
    const list = groups.get(key) ?? [];

    list.push(item);
    groups.set(key, list);
  }

  return [...groups.entries()].map(([section, items]) => ({
    key: section ?? '',
    section,
    items,
  }));
}

export default class ChecklistTemplateModel extends Model {
  @attr('string') name!: string;
  @attr('array') items!: ChecklistTemplateItem[];

  @attr('number') itemsCount!: number;
  @attr('number') requiredItemsCount!: number;
  @attr('number') photoItemsCount!: number;

  @belongsTo('property', { inverse: null, async: false })
  property!: ModelRegistry['property'];

  @cached
  get isPersisted(): boolean {
    return !!this.id;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'checklist-template': ChecklistTemplateModel;
  }
}
